import { useLiveQuery } from 'dexie-react-hooks';
import { Lock, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  activeWithdrawal,
  dryOffDue,
  expectedCalving,
  hasServiceInCycle,
  deriveReproStatus,
  type DomainEvent,
} from '@dairy/domain';
import {
  addDays,
  addMonths,
  birrInputValue,
  CALVING_EASE,
  diffDays,
  DRY_OFF_REASONS,
  eventSchema,
  HEAT_SIGNS,
  newId,
  PREG_CHECK_METHODS,
  SERVICE_METHODS,
  TREATMENT_PROBLEMS,
  TREATMENT_ROUTES,
  tryParseBirr,
  WEIGHT_METHODS,
  type EventRecord,
  type EventType,
} from '@dairy/shared';
import { useIsOwner, useProfile, useSettings } from '@/auth/useSession';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DateField } from '@/components/DateField';
import { IntInput, MoneyInput } from '@/components/inputs';
import { toast } from '@/components/toast';
import { Badge, Button, Card, Checkbox, Dialog, Field, Input, PageHeader, Segmented, Select, Textarea } from '@/components/ui';
import { errorMessage } from '@/components/errors';
import { db, getMeta, setMeta } from '@/db/db';
import { isLocked, type Change } from '@/db/repo';
import { rememberLastUsed, useAnimals, useLastUsed, useToday, type AnimalRow } from '@/db/hooks';
import { useFormat } from '@/lib/format';
import { saveEventWithEffects } from './effects';

const COSTED: EventType[] = ['SERVICE', 'PREG_CHECK', 'TREATMENT', 'VACCINATION', 'DEWORMING'];

interface CalfDraft {
  sex: 'FEMALE' | 'MALE';
  alive: boolean;
  tag: string;
  name: string;
  breed: string;
  animalId?: string;
}

/** The form of one event type (S3-T05 – S3-T07), shared by create and edit. */
export function EventForm({ row, type, existing, protocolId }: { row: AnimalRow; type: EventType; existing?: EventRecord; protocolId: string | null }) {
  const { t } = useTranslation();
  const f = useFormat();
  const s = useSettings();
  const profile = useProfile();
  const isOwner = useIsOwner();
  const today = useToday();
  const navigate = useNavigate();
  const last = useLastUsed();
  const animals = useAnimals() ?? [];
  const protocols = useLiveQuery(() => db.healthProtocols.toArray(), []) ?? [];
  const animal = row.animal;
  const d0 = (existing?.data ?? {}) as Record<string, unknown>;

  const [date, setDate] = useState(existing?.date ?? today);
  const [cost, setCost] = useState(birrInputValue(existing?.costSantim));
  const [note, setNote] = useState(existing?.note ?? '');
  // per-type fields
  const [signs, setSigns] = useState<string[]>((d0.signs as string[]) ?? []);
  const [method, setMethod] = useState<string>((d0.method as string) ?? (type === 'SERVICE' ? (last.serviceMethod ?? 'AI') : type === 'WEIGHT' ? 'TAPE' : 'PALPATION'));
  const [technician, setTechnician] = useState<string>((d0.technician as string) ?? last.technician ?? '');
  const [sireCode, setSireCode] = useState<string>((d0.sireCode as string) ?? '');
  const [straws, setStraws] = useState<string>(d0.straws ? String(d0.straws) : '1');
  const [result, setResult] = useState<string>((d0.result as string) ?? 'PREGNANT');
  const [checkedBy, setCheckedBy] = useState<string>((d0.checkedBy as string) ?? last.checkedBy ?? '');
  const [ease, setEase] = useState<string>((d0.ease as string) ?? 'NORMAL');
  const [placenta, setPlacenta] = useState<string>(d0.placentaExpelled === undefined ? '' : d0.placentaExpelled ? 'yes' : 'no');
  const [calves, setCalves] = useState<CalfDraft[]>(
    ((d0.calves as Array<{ sex: 'FEMALE' | 'MALE'; alive: boolean; animalId?: string }>) ?? [{ sex: 'FEMALE', alive: true }]).map((c) => {
      const a = animals.find((x) => x.id === c.animalId);
      return { sex: c.sex, alive: c.alive, animalId: c.animalId, tag: a?.tag ?? '', name: a?.name ?? '', breed: a?.breed ?? animal.breed };
    }),
  );
  const [reason, setReason] = useState<string>((d0.reason as string) ?? 'PLANNED');
  const [months, setMonths] = useState<string>(d0.monthsPregnant ? String(d0.monthsPregnant) : '');
  const [problem, setProblem] = useState<string>((d0.problem as string) ?? 'MASTITIS');
  const [problemOther, setProblemOther] = useState<string>((d0.problemOther as string) ?? '');
  const [drug, setDrug] = useState<string>((d0.drug as string) ?? '');
  const [dose, setDose] = useState<string>((d0.dose as string) ?? '');
  const [route, setRoute] = useState<string>((d0.route as string) ?? 'INJECTION');
  const [lastDate, setLastDate] = useState<string>((d0.lastDate as string) ?? '');
  const [withdrawal, setWithdrawal] = useState<string>(d0.milkWithdrawalDays !== undefined ? String(d0.milkWithdrawalDays) : '');
  const [givenBy, setGivenBy] = useState<string>((d0.givenBy as string) ?? last.givenBy ?? '');
  const [protocol, setProtocol] = useState<string>((d0.protocolId as string) ?? protocolId ?? '');
  const [product, setProduct] = useState<string>((d0.product as string) ?? '');
  const [kg, setKg] = useState<string>(d0.kg ? String(d0.kg) : '');
  const [text, setText] = useState<string>((d0.text as string) ?? '');
  const [errors, setErrors] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[] | null>(null);
  const [pregnantConfirm, setPregnantConfirm] = useState<{ exp: string; dry: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);

  const locked = !!existing && !isOwner && (existing.isOpening || isLocked(existing, profile, s));
  const others = row.events.filter((e) => e.id !== existing?.id);
  const repro = deriveReproStatus(animal, others, date, s);

  const buildData = (): Record<string, unknown> => {
    const opt = (v: string) => (v.trim() ? v.trim() : undefined);
    switch (type) {
      case 'HEAT':
        return { signs };
      case 'SERVICE':
        return { method, technician: method === 'AI' ? opt(technician) : undefined, sireCode: opt(sireCode), straws: method === 'AI' ? Number(straws) : undefined };
      case 'PREG_CHECK':
        return { result, method, checkedBy: opt(checkedBy) };
      case 'CALVING':
        return { ease, ...(placenta ? { placentaExpelled: placenta === 'yes' } : {}), calves: calves.map((c) => ({ sex: c.sex, alive: c.alive, ...(c.alive && c.animalId ? { animalId: c.animalId } : {}) })) };
      case 'DRY_OFF':
        return { reason };
      case 'ABORTION':
        return months ? { monthsPregnant: Number(months) } : {};
      case 'TREATMENT':
        return {
          problem,
          problemOther: problem === 'OTHER' ? opt(problemOther) : undefined,
          drug: drug.trim(),
          dose: opt(dose),
          route,
          lastDate: lastDate || undefined,
          milkWithdrawalDays: withdrawal === '' ? Number.NaN : Number(withdrawal),
          givenBy: opt(givenBy),
        };
      case 'VACCINATION':
      case 'DEWORMING':
        return { protocolId: protocol || undefined, product: product.trim(), dose: opt(dose), givenBy: opt(givenBy) };
      case 'WEIGHT':
        return { kg: Number(kg.replace(',', '.')), method };
      case 'NOTE':
        return { text: text.trim() };
    }
  };

  const nextCalfTag = (base: string, taken: string[]) => {
    let tag = base;
    let n = 2;
    while (taken.includes(tag.toLowerCase())) tag = `${base}-${n++}`;
    return tag;
  };

  const validate = (): { event: EventRecord; calfRecords: Array<Record<string, unknown>> } | null => {
    const errs: string[] = [];
    const costP = tryParseBirr(cost);
    if (!costP.ok) errs.push(t('events.errors.cost'));
    if (date > today) errs.push(t('errors.DATE_IN_FUTURE'));
    if (date < animal.birthDate) errs.push(t('events.errors.beforeBirth'));
    if (animal.status !== 'ACTIVE' && animal.exitDate && date > animal.exitDate) errs.push(t('errors.ANIMAL_INACTIVE'));
    if (type === 'PREG_CHECK' && !hasServiceInCycle(animal, others, date)) errs.push(t('events.errors.noService'));
    const data = buildData();
    // Live calves get records (AR-3).
    const calfRecords: Array<Record<string, unknown>> = [];
    if (type === 'CALVING') {
      const conceiving = repro?.status === 'PREGNANT' ? others.find((e) => e.id === repro.conceivingServiceId) : undefined;
      const taken = animals.filter((a) => !calves.some((c) => c.animalId === a.id)).map((a) => a.tag.toLowerCase());
      const ids: string[] = [];
      calves.forEach((c, i) => {
        if (!c.alive) {
          ids.push('');
          return;
        }
        const id = c.animalId ?? newId();
        ids.push(id);
        if (!/^[A-Za-z0-9-]{1,20}$/.test(c.tag.trim())) errs.push(t('events.errors.calfTag', { n: i + 1 }));
        if (taken.includes(c.tag.trim().toLowerCase())) errs.push(t('errors.CONFLICT_UNIQUE'));
        taken.push(c.tag.trim().toLowerCase());
        calfRecords.push({
          ...(animals.find((a) => a.id === id) ?? {}),
          id,
          tag: c.tag.trim(),
          name: c.name.trim() || null,
          sex: c.sex,
          breed: c.breed.trim() || animal.breed,
          birthDate: date,
          birthDateEstimated: false,
          damId: animal.id,
          sireCode: ((conceiving?.data as { sireCode?: string } | undefined)?.sireCode ?? null) as string | null,
          source: 'BORN_ON_FARM',
          openingParity: 0,
          status: 'ACTIVE',
        });
      });
      (data.calves as Array<Record<string, unknown>>).forEach((c, i) => {
        if (c.alive) c.animalId = ids[i];
      });
    }
    const event = {
      ...(existing ?? {}),
      id: existing?.id ?? newId(),
      animalId: animal.id,
      type,
      date,
      data,
      costSantim: COSTED.includes(type) && costP.ok ? costP.santim : null,
      isOpening: existing?.isOpening ?? false,
      note: note.trim() || null,
    } as unknown as EventRecord;
    const parsed = eventSchema.safeParse(event);
    if (!parsed.success) errs.push(...parsed.error.issues.slice(0, 3).map((i) => `${t('events.errors.field')}: ${i.path.join('.')}`));
    setErrors(errs);
    return errs.length ? null : { event, calfRecords };
  };

  const softWarnings = (): string[] => {
    const w: string[] = [];
    const lastCalving = [...others].filter((e) => e.type === 'CALVING' && e.date <= date).pop();
    if (type === 'SERVICE') {
      if (!lastCalving && date < addMonths(animal.birthDate, 13)) w.push(t('events.warn.youngHeifer'));
      if (lastCalving && diffDays(date, lastCalving.date) < 30) w.push(t('events.warn.soonAfterCalving'));
      if (repro?.status === 'PREGNANT') w.push(t('events.warn.alreadyPregnant'));
    }
    if (type === 'PREG_CHECK' && repro?.lastServiceDate && diffDays(date, repro.lastServiceDate) < 30) w.push(t('events.warn.checkTooSoon'));
    if (type === 'CALVING') {
      if (repro?.status !== 'PREGNANT' && repro?.status !== 'SERVED') w.push(t('events.warn.calvingNotServed'));
      if (repro?.lastServiceDate && diffDays(date, repro.lastServiceDate) < 240) w.push(t('events.warn.calvingTooSoon'));
    }
    return w;
  };

  const save = async (force = false) => {
    const built = validate();
    if (!built) return;
    if (!force) {
      const w = softWarnings();
      if (w.length) {
        setWarnings(w);
        return;
      }
    }
    setSaving(true);
    try {
      await saveEventWithEffects(built.event, { calves: built.calfRecords });
      if (type === 'SERVICE') {
        await rememberLastUsed('serviceMethod', method);
        if (technician) await rememberLastUsed('technician', technician);
      }
      if (type === 'PREG_CHECK' && checkedBy) await rememberLastUsed('checkedBy', checkedBy);
      if ((type === 'TREATMENT' || type === 'VACCINATION' || type === 'DEWORMING') && givenBy) await rememberLastUsed('givenBy', givenBy);
      const recent = (await getMeta<string[]>('recentAnimals')) ?? [];
      await setMeta('recentAnimals', [animal.id, ...recent.filter((x) => x !== animal.id)].slice(0, 10));
      if (type === 'TREATMENT') {
        const w = activeWithdrawal([...others, built.event as unknown as DomainEvent], date);
        toast(w ? t('events.withheldUntil', { date: f.date(w.until) }) : t('common.saved'));
      } else toast(navigator.onLine ? t('common.saved') : t('common.savedOffline'));
      if (type === 'PREG_CHECK' && result === 'PREGNANT' && repro?.lastServiceDate) {
        const exp = expectedCalving(repro.lastServiceDate, s);
        setPregnantConfirm({ exp, dry: dryOffDue(exp, s) });
        return;
      }
      navigate(`/animals/${animal.id}`, { replace: true });
    } catch (e) {
      toast(errorMessage(t, e), 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!existing) return;
    const after: Change[] = [];
    if (existing.type === 'CALVING') {
      const calfIds = ((existing.data as { calves?: Array<{ animalId?: string }> }).calves ?? []).map((c) => c.animalId).filter((x): x is string => !!x);
      for (const id of calfIds) {
        const has = (await db.events.where('animalId').equals(id).count()) + (await db.milkRecords.where('animalId').equals(id).count());
        if (has > 0) {
          toast(t('errors.HAS_DEPENDENTS'), 'error');
          return;
        }
        for (const r of await db.reminders.where('animalId').equals(id).toArray()) after.push({ entity: 'reminder', op: 'delete', id: r.id });
        after.push({ entity: 'animal', op: 'delete', id });
      }
    }
    try {
      await saveEventWithEffects(existing, { deleted: true, after });
      toast(t('common.deleted'));
      navigate(`/animals/${animal.id}`, { replace: true });
    } catch (e) {
      toast(errorMessage(t, e), 'error');
    }
  };

  return (
    <div className="mx-auto max-w-lg pb-8">
      <PageHeader title={`${t(`events.type.${type}`)} · ${animal.tag}`} />
      {locked ? (
        <p className="mb-3 flex items-center gap-2 rounded-xl bg-stone-200 p-3 text-stone-800">
          <Lock className="h-4 w-4" /> {t('common.lockedAskOwner')}
        </p>
      ) : null}
      {type === 'HEAT' && repro?.status === 'PREGNANT' ? <p className="mb-3 rounded-xl bg-amber-100 p-3 text-amber-950">{t('events.warn.heatWhilePregnant')}</p> : null}
      <fieldset disabled={locked} className="space-y-4">
        <Card className="space-y-4">
          <Field label={t('common.date')}>
            <DateField value={date} max={today} onChange={setDate} />
          </Field>

          {type === 'HEAT' ? (
            <Field label={t('events.signs')}>
              {HEAT_SIGNS.map((x) => (
                <Checkbox key={x} label={t(`events.heatSign.${x}`)} checked={signs.includes(x)} onChange={(e) => setSigns(e.target.checked ? [...signs, x] : signs.filter((y) => y !== x))} />
              ))}
            </Field>
          ) : null}

          {type === 'SERVICE' ? (
            <>
              <Field label={t('events.method')}>
                <Segmented value={method} onChange={setMethod} options={SERVICE_METHODS.map((m) => ({ value: m, label: t(`events.serviceMethod.${m}`) }))} />
              </Field>
              {method === 'AI' ? (
                <>
                  <Field label={t('events.technician')}>
                    <Input value={technician} onChange={(e) => setTechnician(e.target.value)} maxLength={60} />
                  </Field>
                  <Field label={t('events.straws')}>
                    <Segmented value={straws} onChange={setStraws} options={[{ value: '1', label: '1' }, { value: '2', label: '2' }]} />
                  </Field>
                </>
              ) : null}
              <Field label={t('events.sireCode')}>
                <Input value={sireCode} onChange={(e) => setSireCode(e.target.value)} maxLength={40} />
              </Field>
            </>
          ) : null}

          {type === 'PREG_CHECK' ? (
            <>
              <Field label={t('events.result')}>
                <Segmented value={result} onChange={setResult} options={[{ value: 'PREGNANT', label: t('events.checkResult.PREGNANT') }, { value: 'OPEN', label: t('events.checkResult.OPEN') }]} />
              </Field>
              <Field label={t('events.method')}>
                <Select value={method} onChange={(e) => setMethod(e.target.value)}>
                  {PREG_CHECK_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {t(`events.checkMethod.${m}`)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('events.checkedBy')}>
                <Input value={checkedBy} onChange={(e) => setCheckedBy(e.target.value)} maxLength={60} />
              </Field>
            </>
          ) : null}

          {type === 'CALVING' ? (
            <>
              <Field label={t('events.easeLabel')}>
                <Segmented value={ease} onChange={setEase} options={CALVING_EASE.map((m) => ({ value: m, label: t(`events.ease.${m}`) }))} />
              </Field>
              <Field label={t('events.placenta')}>
                <Segmented value={placenta} onChange={setPlacenta} options={[{ value: 'yes', label: t('common.yes') }, { value: 'no', label: t('common.no') }]} />
              </Field>
              {calves.map((c, i) => (
                <Card key={i} className="space-y-3 bg-stone-50">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{t('events.calfN', { n: i + 1 })}</span>
                    {calves.length > 1 && !existing ? (
                      <Button size="sm" variant="ghost" onClick={() => setCalves(calves.filter((_, j) => j !== i))}>
                        {t('common.remove')}
                      </Button>
                    ) : null}
                  </div>
                  <Segmented
                    value={c.sex}
                    onChange={(v) => setCalves(calves.map((x, j) => (j === i ? { ...x, sex: v } : x)))}
                    options={[{ value: 'FEMALE', label: t('animals.female') }, { value: 'MALE', label: t('animals.male') }]}
                  />
                  <Segmented
                    value={c.alive ? 'alive' : 'dead'}
                    onChange={(v) => setCalves(calves.map((x, j) => (j === i ? { ...x, alive: v === 'alive' } : x)))}
                    options={[{ value: 'alive', label: t('events.alive') }, { value: 'dead', label: t('events.dead') }]}
                  />
                  {c.alive ? (
                    <>
                      <Field label={t('animals.tag')}>
                        <Input
                          value={c.tag}
                          placeholder={nextCalfTag(`${animal.tag}-${date.slice(2, 4)}${date.slice(5, 7)}${date.slice(8, 10)}`, animals.map((a) => a.tag.toLowerCase()))}
                          onFocus={() => {
                            if (!c.tag) {
                              const suggested = nextCalfTag(
                                `${animal.tag}-${date.slice(2, 4)}${date.slice(5, 7)}${date.slice(8, 10)}`,
                                [...animals.map((a) => a.tag.toLowerCase()), ...calves.filter((_, j) => j !== i).map((x) => x.tag.toLowerCase())],
                              );
                              setCalves(calves.map((x, j) => (j === i ? { ...x, tag: suggested } : x)));
                            }
                          }}
                          onChange={(e) => setCalves(calves.map((x, j) => (j === i ? { ...x, tag: e.target.value } : x)))}
                          maxLength={20}
                        />
                      </Field>
                      <Field label={t('animals.name')}>
                        <Input value={c.name} onChange={(e) => setCalves(calves.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} maxLength={40} />
                      </Field>
                      <Field label={t('animals.breed')}>
                        <Input value={c.breed} onChange={(e) => setCalves(calves.map((x, j) => (j === i ? { ...x, breed: e.target.value } : x)))} maxLength={60} />
                      </Field>
                    </>
                  ) : null}
                </Card>
              ))}
              {calves.length < 2 && !existing ? (
                <Button variant="secondary" onClick={() => setCalves([...calves, { sex: 'FEMALE', alive: true, tag: '', name: '', breed: animal.breed }])}>
                  {t('events.addTwin')}
                </Button>
              ) : null}
            </>
          ) : null}

          {type === 'DRY_OFF' ? (
            <Field label={t('events.reason')}>
              <Select value={reason} onChange={(e) => setReason(e.target.value)}>
                {DRY_OFF_REASONS.map((m) => (
                  <option key={m} value={m}>
                    {t(`events.dryReason.${m}`)}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}

          {type === 'ABORTION' ? (
            <Field label={t('events.monthsPregnant')}>
              <IntInput value={months} onChange={(e) => setMonths(e.target.value.replace(/\D/g, '').slice(0, 1))} />
            </Field>
          ) : null}

          {type === 'TREATMENT' ? (
            <>
              <Field label={t('events.problemLabel')}>
                <Select value={problem} onChange={(e) => setProblem(e.target.value)}>
                  {TREATMENT_PROBLEMS.map((m) => (
                    <option key={m} value={m}>
                      {t(`events.problem.${m}`)}
                    </option>
                  ))}
                </Select>
              </Field>
              {problem === 'OTHER' ? (
                <Field label={t('events.problemOther')}>
                  <Input value={problemOther} onChange={(e) => setProblemOther(e.target.value)} maxLength={80} />
                </Field>
              ) : null}
              <Field label={t('events.drug')}>
                <Input value={drug} onChange={(e) => setDrug(e.target.value)} maxLength={80} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('events.dose')}>
                  <Input value={dose} onChange={(e) => setDose(e.target.value)} maxLength={40} />
                </Field>
                <Field label={t('events.route')}>
                  <Select value={route} onChange={(e) => setRoute(e.target.value)}>
                    {TREATMENT_ROUTES.map((m) => (
                      <option key={m} value={m}>
                        {t(`events.routeType.${m}`)}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
              <Field label={t('events.lastDate')} hint={t('events.lastDateHint')}>
                {lastDate ? (
                  <DateField value={lastDate} min={date} max={addDays(today, 60)} onChange={setLastDate} />
                ) : (
                  <Button variant="secondary" onClick={() => setLastDate(date)}>
                    {t('events.multiDay')}
                  </Button>
                )}
              </Field>
              <Field label={t('events.withdrawalDays')} hint={t('events.withdrawalHint')}>
                <IntInput value={withdrawal} onChange={(e) => setWithdrawal(e.target.value.replace(/\D/g, '').slice(0, 2))} />
              </Field>
              <Field label={t('events.givenBy')}>
                <Input value={givenBy} onChange={(e) => setGivenBy(e.target.value)} maxLength={60} />
              </Field>
            </>
          ) : null}

          {type === 'VACCINATION' || type === 'DEWORMING' ? (
            <>
              <Field label={t('events.protocol')}>
                <Select
                  value={protocol}
                  onChange={(e) => {
                    setProtocol(e.target.value);
                    const p = protocols.find((x) => x.id === e.target.value);
                    if (p?.product && !product) setProduct(p.product);
                  }}
                >
                  <option value="">{t('common.none')}</option>
                  {protocols
                    .filter((p) => p.type === type && p.active)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                </Select>
              </Field>
              <Field label={t('events.product')}>
                <Input value={product} onChange={(e) => setProduct(e.target.value)} maxLength={80} />
              </Field>
              <Field label={t('events.dose')}>
                <Input value={dose} onChange={(e) => setDose(e.target.value)} maxLength={40} />
              </Field>
              <Field label={t('events.givenBy')}>
                <Input value={givenBy} onChange={(e) => setGivenBy(e.target.value)} maxLength={60} />
              </Field>
            </>
          ) : null}

          {type === 'WEIGHT' ? (
            <>
              <Field label={t('events.kg')}>
                <Input inputMode="decimal" value={kg} onChange={(e) => setKg(e.target.value)} />
              </Field>
              <Field label={t('events.method')}>
                <Segmented value={method} onChange={setMethod} options={WEIGHT_METHODS.map((m) => ({ value: m, label: t(`events.weightMethod.${m}`) }))} />
              </Field>
            </>
          ) : null}

          {type === 'NOTE' ? (
            <Field label={t('events.text')}>
              <Textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} />
            </Field>
          ) : null}

          {COSTED.includes(type) ? (
            <Field label={t('events.costOptional')}>
              <MoneyInput value={cost} onChange={(e) => setCost(e.target.value)} />
            </Field>
          ) : null}
          {type !== 'NOTE' ? (
            <Field label={t('common.note')}>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
            </Field>
          ) : null}
          {existing?.isOpening ? <Badge>{t('events.opening')}</Badge> : null}
        </Card>
        {errors.length ? (
          <ul className="rounded-xl bg-red-50 p-3 text-red-800" role="alert">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        ) : null}
        <Button size="lg" className="w-full" onClick={() => void save()} disabled={saving}>
          {t('common.save')}
        </Button>
        {existing ? (
          <Button variant="ghost" className="w-full" onClick={() => setConfirmDelete(true)}>
            <Trash2 className="h-5 w-5" /> {t('common.delete')}
          </Button>
        ) : null}
      </fieldset>

      <ConfirmDialog open={!!warnings} onOpenChange={(o) => !o && setWarnings(null)} title={t('common.areYouSure')} confirmLabel={t('common.saveAnyway')} onConfirm={() => save(true)}>
        <ul className="list-disc pl-5">
          {(warnings ?? []).map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      </ConfirmDialog>
      <ConfirmDialog open={confirmDelete} onOpenChange={setConfirmDelete} title={t('events.deleteTitle')} danger confirmLabel={t('common.delete')} onConfirm={remove}>
        <p>{t('events.deleteBody')}</p>
      </ConfirmDialog>
      <Dialog
        open={!!pregnantConfirm}
        onOpenChange={(o) => {
          if (!o) navigate(`/animals/${animal.id}`, { replace: true });
        }}
        title={t('events.pregnantTitle', { tag: animal.tag })}
        footer={<Button onClick={() => navigate(`/animals/${animal.id}`, { replace: true })}>{t('common.done')}</Button>}
      >
        {pregnantConfirm ? (
          <dl className="space-y-3">
            <div>
              <dt className="text-sm text-stone-600">{t('breeding.expectedCalving')}</dt>
              <dd className="text-lg font-bold">{f.date(pregnantConfirm.exp)}</dd>
            </div>
            <div>
              <dt className="text-sm text-stone-600">{t('breeding.dryOffDue')}</dt>
              <dd className="text-lg font-bold">{f.date(pregnantConfirm.dry)}</dd>
            </div>
          </dl>
        ) : null}
      </Dialog>
    </div>
  );
}
